import React, { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FiArrowLeft, FiCopy, FiCheck, FiExternalLink, FiSmartphone, FiPhone } from 'react-icons/fi';
import { FlagIcon, FlagIconCode } from 'react-flag-kit';
import {
  useGetTourQuery,
  useGetPartOrderByIdQuery,
  useGetDestinationsQuery,
  useGetCarriersQuery,
  useGetColorsQuery,
} from 'services/kewloxApi';
import { PartOrder } from 'app/models/PartOrder';
import { selectPartOrderById } from 'store/slices/partOrderSlice';
import { AppRootState } from 'index';
import { Delivery } from 'app/models/Delivery';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { getOrderUrlFromPartOrder } from 'app/utils/orderUrl';
import { Badge } from 'components/ui/badge';

interface CopyButtonProps {
  text: string;
  copyText?: string;
  className?: string;
  everCopied: boolean;
  onCopied: () => void;
  title?: string;
}

const CopyButton: React.FC<CopyButtonProps> = ({
  text,
  copyText,
  className = '',
  everCopied,
  onCopied,
  title = 'Copier',
}) => {
  const [justCopied, setJustCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(copyText ?? text);
    } catch {
      const el = document.createElement('textarea');
      el.value = copyText ?? text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    onCopied();
    setJustCopied(true);
    setTimeout(() => setJustCopied(false), 700);
  }, [text, copyText, onCopied]);

  const icon = justCopied ? (
    <FiCheck className="shrink-0 text-green-600 text-sm" />
  ) : everCopied ? (
    <FiCheck className="shrink-0 text-green-500 text-sm" />
  ) : (
    <FiCopy className="shrink-0 text-gray-300 group-hover:text-blue-400 text-sm transition-colors" />
  );

  const textStyle = everCopied && !justCopied
    ? 'text-green-700/60 line-through decoration-green-400/40'
    : '';

  return (
    <button
      onClick={handleCopy}
      className={`inline-flex items-center gap-1 text-left group cursor-pointer hover:text-blue-600 hover:bg-gray-100 rounded px-1 py-0.5 transition-colors ${className}`}
      title={title}
    >
      <span className={`flex-1 ${textStyle}`}>{text}</span>
      {icon}
    </button>
  );
};

interface CopyableSpanProps {
  text: string;
  className?: string;
  everCopied?: boolean;
  onCopied?: () => void;
  title?: string;
}

const CopyableSpan: React.FC<CopyableSpanProps> = ({
  text,
  className = '',
  everCopied = false,
  onCopied,
  title = 'Copier',
}) => {
  const [justCopied, setJustCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    onCopied?.();
    setJustCopied(true);
    setTimeout(() => setJustCopied(false), 700);
  }, [text, onCopied]);

  const textStyle = everCopied && !justCopied
    ? 'text-green-700/60 line-through decoration-green-400/40'
    : '';

  return (
    <span
      onClick={handleCopy}
      className={`cursor-pointer hover:text-blue-600 hover:bg-gray-100 rounded px-0.5 transition-colors ${justCopied ? 'text-green-600' : ''} ${textStyle} ${className}`}
      title={title}
    >
      {text}
    </span>
  );
};

interface CopyIconButtonProps {
  text: string;
  everCopied: boolean;
  onCopied: () => void;
  title?: string;
}

const CopyIconButton: React.FC<CopyIconButtonProps> = ({
  text,
  everCopied,
  onCopied,
  title = 'Copier',
}) => {
  const [justCopied, setJustCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    onCopied();
    setJustCopied(true);
    setTimeout(() => setJustCopied(false), 700);
  }, [text, onCopied]);

  const icon = justCopied ? (
    <FiCheck className="text-green-600 text-sm" />
  ) : everCopied ? (
    <FiCheck className="text-green-500 text-sm" />
  ) : (
    <FiCopy className="text-gray-300 hover:text-blue-400 text-sm transition-colors" />
  );

  return (
    <button
      onClick={handleCopy}
      className="cursor-pointer p-0.5 shrink-0 hover:bg-gray-100 rounded transition-colors"
      title={title}
    >
      {icon}
    </button>
  );
};

interface DeliveryRowProps {
  partOrderId: number;
  position: number;
  copiedFields: Set<string>;
  markCopied: (key: string) => void;
  isActive: boolean;
  onActivate: (partOrderId: number) => void;
}

const DeliveryRow: React.FC<DeliveryRowProps> = ({
  partOrderId,
  position,
  copiedFields,
  markCopied,
  isActive,
  onActivate,
}) => {
  const partOrder = useSelector((state: AppRootState) =>
    selectPartOrderById(state, partOrderId),
  ) as PartOrder | undefined;

  const { isLoading } = useGetPartOrderByIdQuery(partOrderId, {
    skip: !!partOrder?.orderInfo,
  });

  if (isLoading || !partOrder?.orderInfo) {
    return (
      <tr>
        <td colSpan={8} className="px-3 py-2">
          <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse" />
        </td>
      </tr>
    );
  }

  const info = partOrder.orderInfo;
  const cityUpper = (info.city || '').toUpperCase();
  const email = info.email?.trim() ?? '';
  const rawWeight = Number(info.weight);
  const weightValue = Number.isFinite(rawWeight) ? rawWeight.toFixed(2) : '';
  const orderWeight = Number.isFinite(rawWeight) ? `${rawWeight.toFixed(2)} kg` : '—';

  const formatPhone = (phone: string | undefined) => {
    if (!phone || phone === '') return null;
    try {
      const parsed = parsePhoneNumberFromString(phone);
      return parsed ? parsed.formatInternational() : phone;
    } catch {
      return phone;
    }
  };

  const phoneMobile = formatPhone(info.phoneMobile);
  const phone = formatPhone(info.phone);
  const phones = [
    phoneMobile ? { number: phoneMobile, type: 'mobile' as const } : null,
    phone ? { number: phone, type: 'landline' as const } : null,
  ].filter(Boolean) as { number: string; type: 'mobile' | 'landline' }[];

  const nameParts: string[] = [];
  if (info.company) nameParts.push(info.company);
  const fullName =
    `${info.lastname?.toUpperCase() ?? ''} ${info.firstname ?? ''}`.trim();
  if (fullName) nameParts.push(fullName);
  const name = nameParts.join(' — ');

  const addressLines: string[] = [];
  if (info.address2 && info.address2 !== '') addressLines.push(info.address2);
  addressLines.push(info.address1);
  const cityLine = `${info.postcode} ${cityUpper}`;
  addressLines.push(cityLine);
  const fullAddress = [...addressLines, info.isoCode].join(', ');

  const orderName = info.name ?? partOrder.identification;
  const orderNameKey = `${partOrderId}-orderName`;
  const nameKey = `${partOrderId}-name`;
  const addressKey = `${partOrderId}-address`;
  const address2Key = `${partOrderId}-address2`;
  const address1Key = `${partOrderId}-address1`;
  const postcodeKey = `${partOrderId}-postcode`;
  const cityKey = `${partOrderId}-city`;
  const isoCodeKey = `${partOrderId}-isoCode`;
  const emailKey = `${partOrderId}-email`;
  const addressPartKeys = [
    ...(info.address2 && info.address2 !== '' ? [address2Key] : []),
    address1Key,
    postcodeKey,
    cityKey,
    isoCodeKey,
  ];

  // Check if all copyable fields in this row have been copied
  const phoneKeys = phones.map((_, i) => `${partOrderId}-phone-${i}`);
  const allKeys = [
    orderNameKey,
    nameKey,
    addressKey,
    ...addressPartKeys,
    ...phoneKeys,
    ...(email ? [emailKey] : []),
  ];
  const allCopied = allKeys.length > 0 && allKeys.every(k => copiedFields.has(k));
  const rowHoverClass = isActive ? '' : 'hover:bg-gray-50/50';
  const rowBgClass = isActive
    ? 'bg-amber-50/70'
    : allCopied
      ? 'bg-green-50/60'
      : '';

  return (
    <tr
      onClick={() => onActivate(partOrderId)}
      className={`border-b last:border-b-0 ${rowHoverClass} ${rowBgClass}`}
    >
      <td className="px-3 py-2 text-center text-sm text-gray-400 font-medium w-8">
        {position}
      </td>
      <td className="px-3 py-2 text-sm">
        <CopyButton
          text={orderName}
          className="text-sm font-mono text-gray-600"
          everCopied={copiedFields.has(orderNameKey)}
          onCopied={() => markCopied(orderNameKey)}
          title="Copier la commande"
        />
      </td>
      <td className="px-3 py-2 text-sm">
        <CopyButton
          text={name}
          className="text-sm font-semibold text-gray-900 inline-flex"
          everCopied={copiedFields.has(nameKey)}
          onCopied={() => markCopied(nameKey)}
          title="Copier le nom"
        />
      </td>
      <td className="px-3 py-2 text-sm">
        <div className="flex items-center gap-1.5">
          <div className="flex-1 text-gray-700">
            <FlagIcon
              code={info.isoCode as FlagIconCode}
              size={16}
              className="inline-block mr-1.5 align-text-bottom"
            />
            {info.address2 && info.address2 !== '' && (
              <>
                <CopyableSpan
                  text={info.address2}
                  everCopied={copiedFields.has(address2Key)}
                  onCopied={() => markCopied(address2Key)}
                  title="Copier le complément d'adresse"
                />
                {', '}
              </>
            )}
            <CopyableSpan
              text={info.address1}
              everCopied={copiedFields.has(address1Key)}
              onCopied={() => markCopied(address1Key)}
              title="Copier la rue"
            />
            {', '}
            <CopyableSpan
              text={info.postcode}
              className="font-mono"
              everCopied={copiedFields.has(postcodeKey)}
              onCopied={() => markCopied(postcodeKey)}
              title="Copier le code postal"
            />
            {' '}
            <CopyableSpan
              text={cityUpper}
              everCopied={copiedFields.has(cityKey)}
              onCopied={() => markCopied(cityKey)}
              title="Copier la ville"
            />
            {', '}
            <CopyableSpan
              text={info.isoCode}
              className="text-gray-400"
              everCopied={copiedFields.has(isoCodeKey)}
              onCopied={() => markCopied(isoCodeKey)}
              title="Copier le code pays"
            />
          </div>
          <CopyIconButton
            text={fullAddress}
            everCopied={copiedFields.has(addressKey)}
            title="Copier l'adresse complète"
            onCopied={() => {
              markCopied(addressKey);
              addressPartKeys.forEach(markCopied);
            }}
          />
        </div>
      </td>
      <td className="px-3 py-2 text-sm">
        {phones.length > 0 ? (
          <div className="flex flex-col gap-0.5">
            {phones.map((p, i) => {
              const phoneKey = `${partOrderId}-phone-${i}`;
              const PhoneIcon = p.type === 'mobile' ? FiSmartphone : FiPhone;
              return (
                <div key={i} className="inline-flex items-center gap-1.5">
                  <PhoneIcon className="shrink-0 text-gray-400 text-xs" />
                  <CopyButton
                    text={p.number}
                    copyText={p.number.replace(/\s+/g, '')}
                    className="text-sm text-gray-700"
                    everCopied={copiedFields.has(phoneKey)}
                    onCopied={() => markCopied(phoneKey)}
                    title={
                      p.type === 'mobile'
                        ? 'Copier le téléphone mobile'
                        : 'Copier le téléphone fixe'
                    }
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </td>
      <td className="px-3 py-2 text-sm">
        {email ? (
          <CopyButton
            text={email}
            className="text-sm text-gray-700"
            everCopied={copiedFields.has(emailKey)}
            onCopied={() => markCopied(emailKey)}
            title="Copier l'email"
          />
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </td>
      <td className="px-3 py-2 text-sm text-right font-mono text-gray-700">
        {weightValue ? (
          <CopyButton
            text={orderWeight}
            copyText={weightValue}
            className="text-sm text-gray-700"
            everCopied={copiedFields.has(`${partOrderId}-weight`)}
            onCopied={() => markCopied(`${partOrderId}-weight`)}
            title="Copier le poids (valeur numérique)"
          />
        ) : (
          orderWeight
        )}
      </td>
      <td className="px-3 py-2 text-sm text-center">
        <a
          href={getOrderUrlFromPartOrder(partOrder)}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Badge variant="outline" className="gap-0.5 text-xs py-0">
            #{info.idOrder}
            <FiExternalLink className="text-[10px]" />
          </Badge>
        </a>
      </td>
    </tr>
  );
};

const TourCarrierDeliveryInfosPage: React.FC = () => {
  const navigate = useNavigate();
  const { tourId } = useParams<{ tourId: string }>();
  const [copiedFields, setCopiedFields] = useState<Set<string>>(new Set());
  const [activePartOrderId, setActivePartOrderId] = useState<number | null>(null);

  const markCopied = useCallback((key: string) => {
    setCopiedFields(prev => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, []);

  const { isLoading: depsLoading } = useGetDestinationsQuery();
  useGetCarriersQuery();
  useGetColorsQuery();

  const { data: tour, isLoading: tourLoading } = useGetTourQuery(
    Number(tourId),
    { skip: !tourId },
  );

  if (depsLoading || tourLoading || !tour) {
    return (
      <div className="container mx-auto py-12 text-center text-gray-500">
        Chargement de la tournée...
      </div>
    );
  }

  const deliveries = (tour.orders || [])
    .filter((d: Delivery) => d.partOrder)
    .sort((a: Delivery, b: Delivery) => a.position - b.position);

  return (
    <div className="container mx-auto py-4 px-4 max-w-[1800px]">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 mb-3 transition-colors"
      >
        <FiArrowLeft /> Retour
      </button>

      <div className="mb-3 flex items-baseline gap-3">
        <h1 className="text-xl font-bold text-gray-900">
          Tournée #{tourId}
          {tour.carrier && ` — ${tour.carrier.name}`}
        </h1>
        <span className="text-sm text-gray-500">
          {deliveries.length} livraison{deliveries.length > 1 ? 's' : ''}
        </span>
      </div>

      {deliveries.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          Aucune livraison dans cette tournée
        </div>
      ) : (
        <div className="border rounded-lg bg-white overflow-x-auto">
          <table className="w-full min-w-[1400px]">
            <thead>
              <tr className="border-b bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                <th className="px-3 py-2 text-center w-8">#</th>
                <th className="px-3 py-2 text-left">Commande</th>
                <th className="px-3 py-2 text-left">Nom</th>
                <th className="px-3 py-2 text-left">Adresse</th>
                <th className="px-3 py-2 text-left">Tél</th>
                <th className="px-3 py-2 text-left">Email</th>
                <th className="px-3 py-2 text-right">Poids</th>
                <th className="px-3 py-2 text-center">Odoo</th>
              </tr>
            </thead>
            <tbody>
              {deliveries.map((d: Delivery) => (
                <DeliveryRow
                  key={d.id}
                  partOrderId={d.partOrder as number}
                  position={d.position}
                  copiedFields={copiedFields}
                  markCopied={markCopied}
                  isActive={activePartOrderId === (d.partOrder as number)}
                  onActivate={setActivePartOrderId}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TourCarrierDeliveryInfosPage;
